import { useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import {
	LayoutDashboard,
	Users,
	Building2,
	TrendingUp,
	Contact,
	LogOut,
	Shield,
	Menu,
	X,
	ChevronsLeft,
	ChevronsRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useAuthStore } from '@/stores/auth';
import { Button, Logo03Png, Logo01Png, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@hiring-intelligence/ui';

const navigation = [
	{ name: 'Dashboard', href: '/', icon: LayoutDashboard },
	{ name: 'Companies', href: '/companies', icon: Building2 },
	{ name: 'Deals', href: '/deals', icon: TrendingUp },
	{ name: 'Contacts', href: '/contacts', icon: Contact },
	{ name: 'Hiring Requests', href: '/hiring-requests', icon: Users },
	{ name: 'Candidates', href: '/candidates', icon: Users },
];

const superAdminNavigation = [
	{ name: 'User Management', href: '/users', icon: Shield },
];

export default function Layout() {
	const location = useLocation();
	const { user, logout } = useAuthStore();
	const [sidebarOpen, setSidebarOpen] = useState(false);
	const [collapsed, setCollapsed] = useState(false);

	const handleNavClick = () => {
		setSidebarOpen(false);
	};

	const renderNavItem = (item: typeof navigation[0], isCollapsed: boolean) => {
		const isActive = location.pathname === item.href ||
			(item.href !== '/' && location.pathname.startsWith(item.href));

		const link = (
			<Link
				key={item.name}
				to={item.href}
				onClick={handleNavClick}
				className={cn(
					'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
					isCollapsed && 'justify-center px-2',
					isActive
						? 'bg-primary text-primary-foreground'
						: 'text-muted-foreground hover:bg-accent hover:text-accent-foreground'
				)}
			>
				<item.icon className="h-5 w-5 shrink-0" />
				{!isCollapsed && <span className="truncate">{item.name}</span>}
			</Link>
		);

		if (isCollapsed) {
			return (
				<Tooltip key={item.name} delayDuration={0}>
					<TooltipTrigger asChild>{link}</TooltipTrigger>
					<TooltipContent side="right">{item.name}</TooltipContent>
				</Tooltip>
			);
		}

		return link;
	};

	const sidebarInner = (isCollapsed: boolean) => (
		<div className="flex h-full flex-col">
			{/* Logo */}
			<div className={cn('flex h-16 items-center gap-2 border-b', isCollapsed ? 'justify-center px-2' : 'px-6')}>
				<img src={Logo03Png} alt="Canis Major" className="h-8 w-8 shrink-0" />
				{!isCollapsed && <img src={Logo01Png} alt="Canis Major" className="h-8" />}
			</div>

			{/* Navigation */}
			<nav className={cn('flex-1 space-y-1', isCollapsed ? 'p-2' : 'p-4')}>
				{navigation.map((item) => renderNavItem(item, isCollapsed))}
				{user?.role === 'super_admin' && (
					<>
						<div className="my-2 border-t" />
						{superAdminNavigation.map((item) => renderNavItem(item, isCollapsed))}
					</>
				)}
			</nav>

			{/* User section */}
			<div className={cn('border-t', isCollapsed ? 'p-2' : 'p-4')}>
				{isCollapsed ? (
					<Tooltip delayDuration={0}>
						<TooltipTrigger asChild>
							<div className="flex justify-center mb-2">
								<div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
									<span className="text-sm font-medium text-primary">
										{user?.name?.charAt(0).toUpperCase()}
									</span>
								</div>
							</div>
						</TooltipTrigger>
						<TooltipContent side="right">
							<p className="font-medium">{user?.name}</p>
							<p className="text-xs text-muted-foreground">{user?.email}</p>
						</TooltipContent>
					</Tooltip>
				) : (
					<div className="flex items-center gap-3 mb-3">
						<div className="h-8 w-8 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
							<span className="text-sm font-medium text-primary">
								{user?.name?.charAt(0).toUpperCase()}
							</span>
						</div>
						<div className="flex-1 min-w-0">
							<p className="text-sm font-medium truncate">{user?.name}</p>
							<p className="text-xs text-muted-foreground truncate">{user?.email}</p>
						</div>
					</div>
				)}
				{isCollapsed ? (
					<Tooltip delayDuration={0}>
						<TooltipTrigger asChild>
							<Button
								variant="ghost"
								size="sm"
								className="w-full justify-center"
								onClick={logout}
							>
								<LogOut className="h-4 w-4" />
							</Button>
						</TooltipTrigger>
						<TooltipContent side="right">Logout</TooltipContent>
					</Tooltip>
				) : (
					<Button
						variant="ghost"
						size="sm"
						className="w-full justify-start"
						onClick={logout}
					>
						<LogOut className="mr-2 h-4 w-4" />
						Logout
					</Button>
				)}
			</div>
		</div>
	);

	return (
		<TooltipProvider>
			<div className="flex h-screen bg-background">
				{/* Mobile top bar */}
				<div className="fixed top-0 left-0 right-0 z-40 flex h-14 items-center gap-3 border-b bg-card px-4 md:hidden">
					<button
						onClick={() => setSidebarOpen(true)}
						className="rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
					>
						<Menu className="h-6 w-6" />
					</button>
					<img src={Logo03Png} alt="Canis Major" className="h-7 w-7" />
					<img src={Logo01Png} alt="Canis Major" className="h-6" />
				</div>

				{/* Mobile sidebar overlay */}
				{sidebarOpen && (
					<div
						className="fixed inset-0 z-50 bg-black/50 md:hidden"
						onClick={() => setSidebarOpen(false)}
					>
						<aside
							className="absolute left-0 top-0 h-full w-64 max-w-[calc(100vw-3rem)] bg-card shadow-xl"
							onClick={(e) => e.stopPropagation()}
						>
							<button
								onClick={() => setSidebarOpen(false)}
								className="absolute right-3 top-4 rounded-md p-1.5 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
							>
								<X className="h-5 w-5" />
							</button>
							{sidebarInner(false)}
						</aside>
					</div>
				)}

				{/* Desktop sidebar */}
				<aside className={cn(
					'hidden md:flex flex-col border-r bg-card shrink-0 transition-all duration-300 relative',
					collapsed ? 'w-16' : 'w-64'
				)}>
					{sidebarInner(collapsed)}
					{/* Collapse toggle */}
					<button
						onClick={() => setCollapsed(!collapsed)}
						className="absolute -right-3 top-20 z-10 flex h-6 w-6 items-center justify-center rounded-full border bg-card text-muted-foreground shadow-sm hover:bg-accent hover:text-accent-foreground transition-colors"
					>
						{collapsed ? <ChevronsRight className="h-3.5 w-3.5" /> : <ChevronsLeft className="h-3.5 w-3.5" />}
					</button>
				</aside>

				{/* Main content */}
				<main className="flex-1 overflow-auto pt-14 md:pt-0">
					<Outlet />
				</main>
			</div>
		</TooltipProvider>
	);
}
